import { Routes, Route, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, onSnapshot, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db } from './firebaseConfig';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './components/Home';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';

// Import components directly (not lazy loaded) to fix navigation issues
import Auth from './components/Auth';
import PostJob from './components/PostJob';
import JobList from './components/JobList';
import Profile from './components/Profile';
import EditProfile from './components/EditProfile';
import ParentJobs from './components/ParentJobs';
import SitterJobs from './components/SitterJobs';
import Messages from './components/Messages';
// AdminSignUp route removed — it let anyone mint an admin account. New admins
// are created by an existing admin or via a Firebase Console userType edit.
import AdminPanel from './components/AdminPanel';
import Help from './components/Help';

const PUBLIC_ROUTES = ["/", "/auth", "/help"];

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null); // State to store user role
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  // Navigation now uses window.location.href for reliable page changes
  const location = useLocation(); // Get the current location
  const userDocUnsubscribeRef = useRef(null); // Store unsubscribe function for user document
  const messagesUnsubscribesRef = useRef([]); // Store unsubscribe functions for messages listeners

  const getUserData = useCallback(async (uid, retryCount = 0) => {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        return userDoc.data();
      } else if (retryCount < 2) {
        // Document doesn't exist yet — brief retry for signup read-after-write lag.
        // The onSnapshot subscription will catch the doc whenever it's written, so
        // we don't need a long retry chain blocking anything.
        const delay = 1000; // 1s, 1s
        console.log(`User document not found, retrying in ${delay/1000}s (attempt ${retryCount + 1}/2)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return getUserData(uid, retryCount + 1);
      } else {
        console.log("User document not found after 2 retries — onSnapshot will pick it up if/when written.");
        return null;
      }
    } catch (error) {
      console.error("Error fetching user role: ", error);
      return null;
    }
  }, []);

  // Store current pathname in a ref so auth effect doesn't re-run on route changes
  const currentPathnameRef = useRef(location.pathname);
  useEffect(() => {
    currentPathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      // Flip global loading off as soon as we know the auth state — don't block
      // the UI on Firestore reads (which can hang/retry for many seconds).
      if (user) {
        setIsAuthenticated(true);
        setLoading(false);

        if (userDocUnsubscribeRef.current) {
          userDocUnsubscribeRef.current();
          userDocUnsubscribeRef.current = null;
        }
        const userDocRef = doc(db, "users", user.uid);

        // Subscribe to the user doc FIRST so any later writes populate state.
        userDocUnsubscribeRef.current = onSnapshot(userDocRef, (docSnap) => {
          if (!auth.currentUser) {
            return;
          }
          if (docSnap.exists()) {
            const snapshotData = docSnap.data();
            setUserType(snapshotData.userType);
            setUserProfile(snapshotData);
          }
        }, (error) => {
          if (error.code === 'permission-denied') {
            return;
          }
          console.error('Error in user document snapshot:', error);
        });

        // One-shot read in parallel (won't block UI render).
        getUserData(user.uid).then((data) => {
          if (data) {
            setUserType(data.userType);
            setUserProfile(data);
          }
        });

        // Update user email in Firestore if it's verified (non-blocking).
        if (user.emailVerified) {
          updateDoc(doc(db, "users", user.uid), { email: user.email }).catch((error) => {
            console.error("Error updating user email:", error);
          });
        }
      } else {
        setIsAuthenticated(false);
        setUserType(null);
        setUserProfile(null);
        setUnreadMessagesCount(0);
        setLoading(false);
        if (userDocUnsubscribeRef.current) {
          userDocUnsubscribeRef.current();
          userDocUnsubscribeRef.current = null;
        }
        // Redirect to /auth for all routes except public ones
        const isPublic = PUBLIC_ROUTES.includes(currentPathnameRef.current);
        if (!isPublic) {
          window.location.href = '/auth';
        }
      }
    });
    return () => {
      unsubscribe();
      if (userDocUnsubscribeRef.current) {
        userDocUnsubscribeRef.current();
        userDocUnsubscribeRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserData]);

  const handleLogout = async () => {
    try {
      // Step 1: Set isAuthenticated to false FIRST to trigger cleanup in useEffect
      setIsAuthenticated(false);
      setUserType(null);
      setUserProfile(null);
      setUnreadMessagesCount(0);
      
      // Step 2: Clean up ALL listeners manually as well
      if (userDocUnsubscribeRef.current) {
        try {
          userDocUnsubscribeRef.current();
        } catch (error) {
          console.error('Error unsubscribing from user document:', error);
        }
        userDocUnsubscribeRef.current = null;
      }
      
      // Step 3: Clean up all messages listeners
      messagesUnsubscribesRef.current.forEach(unsub => {
        try {
          unsub();
        } catch (error) {
          console.error('Error unsubscribing from messages listener:', error);
        }
      });
      messagesUnsubscribesRef.current = [];
      
      // Step 4: Wait for cleanup to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 5: Sign out (this will trigger onAuthStateChanged which will also clean up)
      // Wrap in try-catch to suppress any remaining permission errors
      try {
        await signOut(auth);
      } catch (signOutError) {
        // Ignore any errors during sign out - we've already cleaned up
        console.log('Sign out completed (some errors may be expected during cleanup)');
      }
      
      // Step 6: Navigate to auth page after sign out completes
      // Use window.location for reliable redirect after logout
      window.location.href = '/auth';
    } catch (error) {
      console.error("Error signing out: ", error);
      // Fallback redirect even on error
      window.location.href = '/auth';
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !auth.currentUser || userType === 'admin') {
      setUnreadMessagesCount(0);
      return;
    }

    let isMounted = true;
    const uid = auth.currentUser.uid;
    // Clear previous unsubscribes
    messagesUnsubscribesRef.current.forEach(unsub => unsub());
    messagesUnsubscribesRef.current = [];
    let totalUnread = 0;

    // Use real-time listeners instead of polling
    const setupUnreadListener = async () => {
      try {
        // Get user's applications once
        const [parentSnap, sitterSnap] = await Promise.all([
          getDocs(query(collection(db, 'applications'), where('parentId', '==', uid))),
          getDocs(query(collection(db, 'applications'), where('sitterId', '==', uid))),
        ]);

        const applicationsMap = new Map();
        parentSnap.forEach((docSnap) => {
          applicationsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
        sitterSnap.forEach((docSnap) => {
          applicationsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });

        const applications = Array.from(applicationsMap.values());
        if (applications.length === 0) {
          if (isMounted) setUnreadMessagesCount(0);
          return;
        }

        const appIds = applications.map((app) => app.id);
        const lastViewed =
          userProfile?.lastMessagesViewedAt?.toDate
            ? userProfile.lastMessagesViewedAt.toDate()
            : userProfile?.lastMessagesViewedAt ?? null;

        // Set up real-time listeners for messages (chunked due to Firestore limits)
        const chunkSize = 10;
        const unreadCounts = new Map(); // Track unread count per chunk

        const updateTotalUnread = () => {
          totalUnread = Array.from(unreadCounts.values()).reduce((sum, count) => sum + count, 0);
          if (isMounted) {
            setUnreadMessagesCount(totalUnread);
          }
        };

        // Listen to messages for each chunk
        for (let i = 0; i < appIds.length; i += chunkSize) {
          const chunk = appIds.slice(i, i + chunkSize);
          const chunkKey = `chunk_${i}`;
          const messagesQuery = query(
            collection(db, 'messages'),
            where('applicationId', 'in', chunk),
            orderBy('timestamp', 'desc')
          );

          // Capture isMounted value at loop iteration to avoid closure warning
          const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
            // Check if user is still authenticated before processing
            if (!isMounted || !auth.currentUser) {
              return;
            }
            
            let chunkUnread = 0;
            snapshot.forEach((docSnap) => {
              const data = docSnap.data();
              if (!data || data.senderId === uid) return;
              const timestamp = data.timestamp?.toDate ? data.timestamp.toDate() : null;
              if (!lastViewed || (timestamp && timestamp > lastViewed)) {
                chunkUnread += 1;
              }
            });
            
            unreadCounts.set(chunkKey, chunkUnread);
            updateTotalUnread();
          }, (error) => {
            // Handle permission errors gracefully (e.g., during sign out)
            if (error.code === 'permission-denied') {
              // Silently ignore permission errors during sign out
              return;
            }
            console.error('Error in messages snapshot:', error);
          });
          
          messagesUnsubscribesRef.current.push(unsubscribe);
        }
      } catch (error) {
        console.error('Error setting up unread messages listener:', error);
      }
    };

    setupUnreadListener();

    return () => {
      isMounted = false;
      messagesUnsubscribesRef.current.forEach(unsub => unsub());
      messagesUnsubscribesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userType, userProfile?.lastMessagesViewedAt]);

  // This onAuthStateChanged listener is handled in the useEffect above
  // No need for a separate listener here

  if (loading) {
    // Render a loading indicator while checking authentication state
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {isAuthenticated && (
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img
                src="/logo.png"
                alt="Home"
                className="w-16 h-16 mr-2"
              />
              <span className="sr-only">Home</span>
            </a>
        
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="text-gray-600 focus:outline-none"
              >
                {menuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
        
            {/* Desktop Menu - Using window.location for reliable navigation */}
            <ul className="hidden md:flex items-center space-x-6 text-sm md:text-base">
              <li>
                <a href={`/profile/${auth.currentUser?.uid}`} className="text-gray-600 hover:text-blue-600 transition cursor-pointer">Profile</a>
              </li>
              {userType === 'parent' && (
                <li>
                  <a href="/post-job" className="text-gray-600 hover:text-blue-600 transition cursor-pointer">Post A Job</a>
                </li>
              )}

              
              <li>
                <a href="/jobs" className="text-gray-600 hover:text-blue-600 transition cursor-pointer">Jobs List</a>
              </li>

              {userType !== 'admin' && (
                <li>
                  <a href={userType === 'parent' ? '/parent-jobs' : '/sitter-jobs'} className="text-gray-600 hover:text-blue-600 transition cursor-pointer">Your Jobs</a>
                </li>
              )}
              
              {userType !== 'admin' && (
                <li>
                  <a href="/messages" className="text-gray-600 hover:text-blue-600 transition flex items-center gap-2 cursor-pointer">
                    <span>Messages</span>
                    {unreadMessagesCount > 0 && (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
                        {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                      </span>
                    )}
                  </a>
                </li>
              )}

              {userType === 'admin' && (
                <li>
                  <a href="/admin-panel" className="text-gray-600 hover:text-blue-600 transition cursor-pointer">Admin Panel</a>
                </li>
              )}

              <li>
                <a href="/help" className="text-gray-600 hover:text-blue-600 transition cursor-pointer">Help</a>
              </li>

            </ul>
        
            {/* Desktop Auth Button */}
            <div className="hidden md:block ml-4">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition"
                >
                  Logout
                </button>
              ) : (
                <a
                  href="/auth"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition"
                >
                  Login
                </a>
              )}
            </div>
          </div>
        
          {/* Mobile Menu Dropdown - Using regular anchors for reliable navigation */}
          {menuOpen && (
            <div className="md:hidden mt-4 bg-white rounded-lg shadow-lg py-4 px-6 space-y-3 transition-all duration-300">
              <a 
                href={`/profile/${auth.currentUser?.uid}`} 
                className="block text-gray-700 font-medium hover:text-blue-600 transition"
              >
                Profile
              </a>
              {userType === 'parent' && (
                <a 
                  href="/post-job" 
                  className="block text-gray-700 font-medium hover:text-blue-600 transition"
                >
                  Post A Job
                </a>
              )}
              <a 
                href="/jobs" 
                className="block text-gray-700 font-medium hover:text-blue-600 transition"
              >
                Jobs List
              </a>
              {userType !== 'admin' && (
                <a 
                  href={userType === 'parent' ? '/parent-jobs' : '/sitter-jobs'} 
                  className="block text-gray-700 font-medium hover:text-blue-600 transition"
                >
                  Your Jobs
                </a>
              )}
              {userType !== 'admin' && (
                <a 
                  href="/messages" 
                  className="block text-gray-700 font-medium hover:text-blue-600 transition flex items-center justify-between"
                >
                  <span>Messages</span>
                  {unreadMessagesCount > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full ml-3">
                      {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                    </span>
                  )}
                </a>
              )}
              {userType === 'admin' && (
                <a 
                  href="/admin-panel" 
                  className="block text-gray-700 font-medium hover:text-blue-600 transition"
                >
                  Admin Panel
                </a>
              )}
              <a 
                href="/help" 
                className="block text-gray-700 font-medium hover:text-blue-600 transition"
              >
                Help
              </a>
              <hr className="border-t border-gray-200" />
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="w-full bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition"
                >
                  Logout
                </button>
              ) : (
                <a 
                  href="/auth"
                  className="block w-full bg-blue-600 text-white py-2 rounded-lg text-center hover:bg-blue-700 transition"
                >
                  Login
                </a>
              )}
            </div>
          )}
        </nav>
      )}
  
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/post-job" element={<ProtectedRoute><PostJob /></ProtectedRoute>} />
          <Route path="/jobs" element={<ProtectedRoute><JobList /></ProtectedRoute>} />
          <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
          <Route path="/parent-jobs" element={<ProtectedRoute><ParentJobs /></ProtectedRoute>} />
          <Route path="/sitter-jobs" element={<ProtectedRoute><SitterJobs /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/admin-panel" element={<ProtectedAdminRoute><AdminPanel /></ProtectedAdminRoute>} />
          <Route path="/help" element={<Help />} />
        </Routes>
      </div>
    </div>
  );
  
  
}

export default App;